/* ───────────────────────────────────────────────────────────────────────────
 * notificationsAction.js — STATIC (3PL Logistics static dashboard branch)
 *
 * The notifications module on this branch is fully static: NO backend / API
 * calls are made. Every action-creator below dispatches hardcoded fixtures
 * from ./staticNotificationsData. The loader-gated fetches (notifications,
 * preferences, distribution, roles-by-event) resolve after a 1-second delay so
 * the existing screen loaders still display before the static data appears.
 * ─────────────────────────────────────────────────────────────────────────── */

import {
  FETCH_NOTIFICATIONS_LOADING,
  FETCH_NOTIFICATIONS,
  MARK_NOTIFICATION_READ,
  MARK_ALL_NOTIFICATIONS_READ,
  TOGGLE_NOTIFICATION_STAR,
  FETCH_UNREAD_COUNT,
  CLEAR_NOTIFICATIONS_STATE,
  FETCH_PREFERENCE_EVENT_TYPES,
  FETCH_PREFERENCES_LOADING,
  FETCH_PREFERENCES,
  UPDATE_PREFERENCES,
  FETCH_DISTRIBUTION_LOADING,
  FETCH_DISTRIBUTION,
  UPDATE_DISTRIBUTION,
  UPLOAD_DISTRIBUTION,
  DOWNLOAD_DISTRIBUTION_LOADING,
  DOWNLOAD_DISTRIBUTION,
  CLEAR_DISTRIBUTION_STATE,
  FETCH_ORG_DEPT_ROLES,
  FETCH_CMO_DEPT_ROLES,
  FETCH_NOTIFICATION_CATEGORIES,
  FETCH_ROLES_BY_EVENT,
  FETCH_ROLES_BY_EVENT_LOADING,
} from "../types";

import {
  STATIC_EVENT_ROLES,
  STATIC_ORG_DEPT_ROLES,
  STATIC_FEED_IDS,
  getStaticCategories,
  getStaticPreferenceGroups,
  getStaticDistributionResponse,
  getStaticNotificationsResponse,
  getStaticUnreadCount,
  markStaticRead,
  markAllStaticRead,
  toggleStaticStar,
} from "./staticNotificationsData";

// 1-second loader delay, matching every static screen.
const LOADER_MS = 1000;

// ─── 1. Fetch notifications list (static feed) ───────────────────────────────
// Kept exported for components that cancel an in-flight fetch on unmount.
export let fetchNotificationsSource = { cancel: () => {} };
export const fetchNotifications = (params = {}) => (dispatch) => {
  dispatch({ type: FETCH_NOTIFICATIONS_LOADING });
  const timer = setTimeout(() => {
    dispatch({ type: FETCH_NOTIFICATIONS, payload: getStaticNotificationsResponse(params) });
  }, LOADER_MS);
  fetchNotificationsSource = { cancel: () => clearTimeout(timer), token: null };
};

// ─── 2. Fetch single notification by ID ──────────────────────────────────────
// Static feed rows already carry their full payload, so this is a no-op.
export const fetchNotificationById = () => () => {};

// ─── 3. Fetch unread count (static badge) ─────────────────────────────────────
export const fetchUnreadCount = () => (dispatch) => {
  dispatch({ type: FETCH_UNREAD_COUNT, payload: getStaticUnreadCount() });
};

// ─── 4. Mark single notification as read (UI only) ───────────────────────────
export const markNotificationRead = (data) => (dispatch) => {
  markStaticRead(data.id);
  dispatch({ type: MARK_NOTIFICATION_READ, payload: { id: data.id } });
};

// ─── 5. Mark all notifications as read (UI only) ─────────────────────────────
export const markAllNotificationsRead = () => (dispatch) => {
  markAllStaticRead();
  dispatch({ type: MARK_ALL_NOTIFICATIONS_READ, payload: { updated: STATIC_FEED_IDS.length } });
};

// ─── 6. Toggle star (UI only) ─────────────────────────────────────────────────
export const toggleNotificationStar = (data) => (dispatch) => {
  const isStarred = toggleStaticStar(data.id);
  dispatch({ type: TOGGLE_NOTIFICATION_STAR, payload: { id: data.id, isStarred } });
};

// ─── 7. Clear notification state flags ────────────────────────────────────────
export const clearNotificationsState = () => ({
  type: CLEAR_NOTIFICATIONS_STATE,
});

// ─── 8. Fetch preference event types (static, rarely used) ───────────────────
export const fetchPreferenceEventTypes = () => (dispatch) => {
  const flat = getStaticPreferenceGroups().flatMap((g) =>
    (g.eventNotifications || []).map((en) => ({
      event_type_id: en.eventTypeID,
      event_name: en.eventType,
      category: g.objectType,
    }))
  );
  dispatch({ type: FETCH_PREFERENCE_EVENT_TYPES, payload: flat });
};

// ─── 9. Fetch user notification preferences (Manage Notifications) ───────────
export const fetchPreferences = () => (dispatch) => {
  dispatch({ type: FETCH_PREFERENCES_LOADING });
  setTimeout(() => {
    dispatch({ type: FETCH_PREFERENCES, payload: getStaticPreferenceGroups() });
  }, LOADER_MS);
};

// ─── 10. Update user notification preferences (UI only) ──────────────────────
export const updatePreferences = (mappings) => (dispatch) => {
  dispatch({ type: UPDATE_PREFERENCES, payload: mappings });
};

// ─── 11. Fetch distribution list (Manage Distribution List) ──────────────────
export const fetchDistribution = () => (dispatch) => {
  dispatch({ type: FETCH_DISTRIBUTION_LOADING });
  setTimeout(() => {
    dispatch({ type: FETCH_DISTRIBUTION, payload: getStaticDistributionResponse() });
  }, LOADER_MS);
};

// ─── 12. Update distribution roles for an event (UI only) ────────────────────
export const updateDistribution = (data) => (dispatch) => {
  dispatch({ type: UPDATE_DISTRIBUTION, payload: data });
};

// ─── 13. Upload distribution (static — just report success) ──────────────────
export const uploadDistribution = () => (dispatch) => {
  dispatch({ type: UPLOAD_DISTRIBUTION, payload: { status: true } });
};

// ─── 14. Download event notifications (handled client-side in component) ─────
export const downloadDistribution = () => (dispatch) => {
  dispatch({ type: DOWNLOAD_DISTRIBUTION_LOADING });
  dispatch({ type: DOWNLOAD_DISTRIBUTION, payload: null });
};

// ─── 15. Clear distribution state flags ──────────────────────────────────────
export const clearDistributionState = () => ({
  type: CLEAR_DISTRIBUTION_STATE,
});

// ─── 16a. Fetch CMO departments + roles (static) ─────────────────────────────
export const fetchCmoDeptRoles = () => (dispatch) => {
  dispatch({
    type: FETCH_CMO_DEPT_ROLES,
    payload: {
      departments: (STATIC_ORG_DEPT_ROLES.departments || []).map((d) => ({
        departmentName: d.departmentName,
        rolesList: (STATIC_ORG_DEPT_ROLES.roles || []).map((r) => ({ role_name: r.role_name })),
      })),
    },
  });
};

// ─── 16b. Fetch org / dept / roles for distribution edit popup (static) ──────
export const fetchOrgDeptRoles = () => (dispatch) => {
  dispatch({ type: FETCH_ORG_DEPT_ROLES, payload: STATIC_ORG_DEPT_ROLES });
};

// ─── 17. Fetch notification categories (static) ──────────────────────────────
export const fetchNotificationCategories = () => (dispatch) => {
  dispatch({ type: FETCH_NOTIFICATION_CATEGORIES, payload: getStaticCategories() });
};

// ─── 18. Fetch roles for a specific event (eye/edit popup) — same for all ────
export const fetchRolesByEvent = () => (dispatch) => {
  dispatch({ type: FETCH_ROLES_BY_EVENT_LOADING });
  setTimeout(() => {
    dispatch({ type: FETCH_ROLES_BY_EVENT, payload: STATIC_EVENT_ROLES });
  }, LOADER_MS);
};
